import React, { useState } from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import {
  useQuery,
  useMutation,
  useQueryClient
} from '@sopranodesign/pulse-hooks';
import {
  Button,
  ButtonSelect,
  Checkbox,
  Divider,
  Flex,
  Heading3,
  Label,
  NoResults,
  Placeholder,
  Span,
  TextInput,
  useNotification
} from '@sopranodesign/pulse-components';

import { createDashboardShare, revokeDashboardShare } from '../api/index';

const EXPIRY_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '1 year' }
];

const RANGE_PRESETS = [
  { value: 'last_hour', label: 'Last hour (rolling)' },
  { value: 'last_day', label: 'Last day (rolling)' },
  { value: 'last_week', label: 'Last week (rolling)' },
  { value: 'last_month', label: 'Last month (rolling)' }
];

const formatDate = date => dayjs(date).format('D MMM YYYY HH:mm');

const buildLink = token =>
  `${window.location.origin}/shared/dashboard/${token}`;

const ShareDashboard = ({ start, end }) => {
  const queryClient = useQueryClient();

  const { showSuccess, showError } = useNotification();

  const rangeOptions = [
    {
      value: 'fixed',
      label: `Fixed: ${formatDate(start)} - ${formatDate(end)}`
    },
    ...RANGE_PRESETS
  ];

  const [range, setRange] = useState(rangeOptions[0]);
  const [expiry, setExpiry] = useState(EXPIRY_OPTIONS[1]);
  const [compare, setCompare] = useState(false);
  const [link, setLink] = useState(null);

  const shares = useQuery({ queryKey: ['dashboard-shares'] });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['dashboard-shares'] });

  const create = useMutation({
    mutationFn: createDashboardShare,
    onSuccess: response => {
      setLink(buildLink(response.data.meta.token));
      refresh();
    }
  });

  const revoke = useMutation({
    mutationFn: revokeDashboardShare,
    onSuccess: () => {
      showSuccess('The link has been revoked.');
      refresh();
    }
  });

  const handleCreate = () => {
    setLink(null);

    create.mutate({
      expires_in_days: expiry.value,
      compare,
      ...(range.value === 'fixed'
        ? { start: new Date(start).toISOString(), end: new Date(end).toISOString() }
        : { preset: range.value })
    });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      showSuccess('Link copied to your clipboard.');
    } catch {
      showError('Unable to copy the link. Select it and copy it manually.');
    }
  };

  const activeShares = shares.data?.data || [];

  return (
    <Flex col ySpace>
      <Span light>
        Anyone with the link can view this dashboard without logging in. Metrics
        that list individual people are never included.
      </Span>

      <Flex col fat="true">
        <Label htmlFor="share-range">Time range</Label>
        <ButtonSelect
          id="share-range"
          aria-label="Time range"
          options={rangeOptions}
          value={range}
          onChange={setRange}
        />
      </Flex>

      <Flex col fat="true">
        <Label htmlFor="share-expiry">Link expires after</Label>
        <ButtonSelect
          id="share-expiry"
          aria-label="Link expires after"
          options={EXPIRY_OPTIONS}
          value={expiry}
          onChange={setExpiry}
        />
      </Flex>

      <Checkbox
        checked={compare}
        onChange={() => setCompare(current => !current)}
        label="Compare with previous period"
      />

      <Button
        variant="primary"
        icon="link"
        loading={create.isPending}
        disabled={create.isPending}
        onClick={handleCreate}>
        Create link
      </Button>

      {link && (
        <Flex col ySpaceSm role="status">
          <Label htmlFor="share-link">
            Your link (it won&apos;t be shown again)
          </Label>
          <Flex xSpaceSm>
            <TextInput
              id="share-link"
              readOnly
              value={link}
              onFocus={event => event.target.select()}
            />
            <Button variant="secondary" onClick={handleCopy}>
              Copy
            </Button>
          </Flex>
        </Flex>
      )}

      <Divider />

      <Heading3>Active links</Heading3>
      {shares.isLoading && <Placeholder />}
      {!shares.isLoading && activeShares.length === 0 && (
        <NoResults text="No active links" />
      )}
      {activeShares.map(share => (
        <Flex between center key={share.id}>
          <Flex col>
            <Span>
              {share.preset
                ? RANGE_PRESETS.find(({ value }) => value === share.preset)
                    ?.label
                : `${formatDate(share.start)} - ${formatDate(share.end)}`}
            </Span>
            <Span light tinyText>
              Created {formatDate(share.created_at)}
              {share.created_by ? ` by ${share.created_by}` : ''}. Expires{' '}
              {formatDate(share.expires_at)}.{' '}
              {share.last_viewed_at
                ? `Last viewed ${formatDate(share.last_viewed_at)}.`
                : 'Not viewed yet.'}
            </Span>
          </Flex>
          <Button
            variant="secondary"
            colour="danger"
            disabled={revoke.isPending}
            aria-label={`Revoke link created ${formatDate(share.created_at)}`}
            onClick={() => revoke.mutate(share.id)}>
            Revoke
          </Button>
        </Flex>
      ))}
    </Flex>
  );
};

ShareDashboard.propTypes = {
  start: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  end: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired
};

export default ShareDashboard;
